import debounce from 'lodash/debounce'
import _get from 'lodash/get'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { UseFormReturn } from 'react-hook-form'

import type { Language, SupportedMarketKey } from '@nxt-insurance/constants'

const FORM_FLUSH_DELAY_MS = 100
const QUOTE_REFETCH_DEBOUNCE_MS = 250

interface FetchPriceInput {
  language: Language
  market: SupportedMarketKey
  values: Record<string, unknown>
  stepName: string
}

interface UseQuotePriceRefetchOptions {
  form: Pick<UseFormReturn<any>, 'getValues' | 'watch'>
  language: Language
  market: SupportedMarketKey
  stepName: string
  quoteStepName: string
  quoteConfigPathPrefix: string
  livePricePaths?: readonly string[]
  livePriceGuards?: Readonly<Record<string, (value: unknown) => boolean>>
  mutatePrice: (input: FetchPriceInput) => Promise<unknown>
  resetPrice: () => void
}

export const useQuotePriceRefetch = (options: UseQuotePriceRefetchOptions) => {
  const {
    form,
    language,
    market,
    stepName,
    quoteStepName,
    quoteConfigPathPrefix,
    livePricePaths,
    livePriceGuards,
    mutatePrice,
    resetPrice,
  } = options

  const prevStepNameRef = useRef(stepName)
  useEffect(() => {
    if (stepName === quoteStepName && prevStepNameRef.current !== quoteStepName) {
      resetPrice()
    }
    prevStepNameRef.current = stepName
  }, [stepName, quoteStepName, resetPrice])

  const debouncing = useRef(false)
  const requestsPending = useRef(0)
  const [isPriceRequestPending, setPriceRequestPending] = useState(false)
  const syncPriceRequestPending = useCallback(() => {
    setPriceRequestPending(debouncing.current || requestsPending.current > 0)
  }, [])

  const refetchPrice = useCallback(() => {
    debouncing.current = false
    requestsPending.current += 1
    syncPriceRequestPending()
    setTimeout(() => {
      void mutatePrice({
        language,
        market,
        values: form.getValues() as Record<string, unknown>,
        stepName,
      })
        .catch(() => undefined)
        .finally(() => {
          requestsPending.current -= 1
          syncPriceRequestPending()
        })
    }, FORM_FLUSH_DELAY_MS)
  }, [form, language, market, stepName, mutatePrice, syncPriceRequestPending])

  const debouncedRefetchPrice = useMemo(
    () => debounce(refetchPrice, QUOTE_REFETCH_DEBOUNCE_MS),
    [refetchPrice]
  )

  useEffect(() => {
    const pricePaths = [
      ...(stepName === quoteStepName ? [quoteConfigPathPrefix] : []),
      ...(livePricePaths ?? []),
    ]
    const subscription = form.watch((values, meta) => {
      const changedPath = meta.name ?? ''
      if (!pricePaths.some((path) => changedPath.startsWith(path))) {
        return
      }
      const isPriceable = Object.entries(livePriceGuards ?? {}).every(([path, isReady]) =>
        isReady(_get(values, path))
      )
      if (!isPriceable) {
        return
      }
      debouncing.current = true
      syncPriceRequestPending()
      debouncedRefetchPrice()
    })

    return () => {
      subscription.unsubscribe()
      debouncedRefetchPrice.cancel()
      debouncing.current = false
      syncPriceRequestPending()
    }
  }, [
    form,
    stepName,
    quoteStepName,
    quoteConfigPathPrefix,
    livePricePaths,
    livePriceGuards,
    debouncedRefetchPrice,
    syncPriceRequestPending,
  ])

  return { refetchPrice, isPriceRequestPending }
}
